import {
  BorderRadiusStyles,
  ButtonConfigurations,
  ButtonLoadingIndicatorType,
  ButtonSizeOptions,
  ButtonTypes,
  ConfirmationDialogStyleConfigurations,
  GlobalThemeConfiguration,
  InputFieldsConfigurations,
  InputLabelOptions,
  NewColourPaletteType,
  TextSkeletonType,
  TooltipStyleConfigurations,
  WidgetNames,
} from '../config.type';
import { NewWorkflowWidgetConfig } from '../workflow.v2.type';
import { BorderRadiusKeys, ButtonHeightSizeOptions, TypographyKeys } from './global-styling.enum';

export const defaultColourPalette: NewColourPaletteType = {
  main: {
    primary: '#3574FC',
    background: '#F6F6F6',
    surface: '#FFFFFF',
  },
  typography: {
    onPrimary: '#FFFFFF',
    onPrimaryMuted: '#F5F5F5',
    onBackground: '#000B0C',
    onBackgroundMuted: '#757575',
    onSurface: '#000B0C',
    onSurfaceMuted: '#757575',
  },
  states: {
    active: '#9747FF',
    hover: '#023197',
  },
  status: {
    error: '#EF0606',
    success: '#1BB12F',
    info: '#FFF7CF',
    warning: '#FFC008',
  },
  border: {
    default: '#CED4DA',
  },
  customColors: [], //todo: needs removing - custom groups will be added to the root instead
};

export const defaultExtraCss = `:root {
    /* this navbar height is used as margin from bottom for position:fixed with anchored buttons*/
    --mobile-navbar-height: 71px;
    /* this navbar height is used for subtracting this height from 100vh for the page contents section */
    --vh-navbar-height: 99px;
    /* used on relative desktop headers */
    --primary-full-width-average-height: 46px;
    /* used for subtracting this height from 100vh for the page contents section depending on how many buttons there are */
    --mobile-one-fixed-button: 165px;
    --mobile-two-fixed-button: 152px;
    --mobile-three-fixed-button: 188px;
    /* space between the fixed buttons */
    --mobile-button-spacer: 20px;
    /* space between the navbar->buttons and button->page contents */
    --mobile-page-pb: 20px;
    /* padding to side of fixed buttons so same size as everything else on page */
    --mobile-page-pl: 1em;
    --mobile-page-pr: 1em;
    /* mobile section padding */
    --mobile-section-p: 1em;
    --mobile-section-pl: 1em;
    --mobile-section-pr: 1em;
    /* mobile border radius*/
    --mobile-section-r: 12px;
    /* mobile title margin space*/
    --mobile-title-mb: 20px;
    /* used as margin left from fixed page header */
    --navbar-width: 192px;
    /* width of back/next buttons on desktop */
    --desktop-form-button-width: 120px;
    /* desktop border radius*/
    --desktop-section-radius: 12px;
    /* used as side padding for page contents, depending on what is needed */
    --desktop-page-pt: 6rem;
    --desktop-page-pl: 4rem;
    --desktop-page-pr: 4rem;
    --desktop-pl-l: 30vw;
    --desktop-pr-l: 30vw;
    --desktop-pl-m: 20vw;
    --desktop-pr-m: 20vw;
    --desktop-pl-s: 10vw;
    --desktop-pr-s: 10vw;
    /* padding top and bottom for sections where page contents scroll instead of being vertically centered */
    --desktop-scroll-pt: 4rem;
    --desktop-scroll-pb: 10vh;
    /* Scrollbars */
    --scrollbar-thumb-color: rgba(0, 0, 0, 0.3);
}

  #web-app .otp-input-field .ng-otp-input-wrapper .otp-input {
      padding-left: 0px !important;
  }

  finxone-web-frontend-phone-number .verify-button {
      height: --verify-button-height !important;
      border-radius: --verify-button-border-radius !important;
  }

  finxone-web-frontend-email .verify-button {
      height: --verify-button-height !important;
      border-radius: --verify-button-border-radius !important;
  }

  #web-app finx-table .search-input {
      padding-right: 0px !important;
      padding-left: 0px !important;
  }

  .navbar {
      background: var(--color-surface) !important;
  }

  .navbar-item:hover {
      background: var(--color-hover) !important;
  }

  #web-app table tr:hover {
      background: var(--color-hover) !important;
  }

  .transaction-header {
    margin-bottom: 0px !important;
  }

  #web-app .expiry-counter h2 {
    font-size: 18px !important;
  }

  #web-app .p-inputgroup-addon {
      background: none !important;
  }
`;

export const defaultLoaderCss = `
  .loader-container {
    position: absolute;
    top: 50%;
    right: 50%;
    transform: translate(+25px, -25px);
  }

  .loader {
    width: 50px;
    aspect-ratio: 1;
    border-radius: 50%;
    background: radial-gradient(farthest-side, #5e6566 94%, #0000) top/8px 8px no-repeat,
      conic-gradient(#0000 30%, #5e6566);
    -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 8px), #000 0);
    animation: l13 1s infinite linear;
  }

  @keyframes l13 {
    100% {
      transform: rotate(1turn);
    }
  }
`;

export const defaultGlobalThemeGeneralConfig: Pick<GlobalThemeConfiguration, 'generalConfigurations'> = {
  generalConfigurations: {
    companyInfo: {
      name: 'Finxone',
      legalEntityName: 'Finxone Ltd',
      logo: '',
      logoTitle: '',
      logoFileName: '',
    },
    defaultSystemFont: false,
    fontFamily: 'Mulish',
    secondaryFontFamily: 'Mulish',
    palette: defaultColourPalette,
    loaderCss: defaultLoaderCss,
    extraCss: defaultExtraCss,
  },
};

export const defaultGlobalThemeTypographyConfig: Pick<GlobalThemeConfiguration, 'typography'> = {
  typography: {
    [TypographyKeys.HEADING1]: {
      'font-family': 'var(--font-family)',
      'font-weight': '700',
      'font-size': '72px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.25',
      'margin-bottom': '0px',
    },
    [TypographyKeys.HEADING2]: {
      'font-family': 'var(--font-family)',
      'font-weight': '700',
      'font-size': '60px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.2',
      'margin-bottom': '0px',
    },
    [TypographyKeys.HEADING3]: {
      'font-family': 'var(--font-family)',
      'font-weight': '700',
      'font-size': '48px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.25',
      'margin-bottom': '0',
    },
    [TypographyKeys.HEADING4]: {
      'font-family': 'var(--font-family)',
      'font-weight': '700',
      'font-size': '36px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.22',
      'margin-bottom': '0px',
    },
    [TypographyKeys.HEADING5]: {
      'font-family': 'var(--font-family)',
      'font-weight': '700',
      'font-size': '30px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.27',
      'margin-bottom': '0px',
    },
    [TypographyKeys.HEADING6]: {
      'font-family': 'var(--font-family)',
      'font-weight': '700',
      'font-size': '24px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.33',
      'margin-bottom': '0px',
    },
    [TypographyKeys.SUBTITLE1]: {
      'font-family': 'var(--font-family)',
      'font-weight': '400',
      'font-size': '18px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.56',
      'margin-bottom': '0px',
    },
    [TypographyKeys.SUBTITLE2]: {
      'font-family': 'var(--font-family)',
      'font-weight': '400',
      'font-size': '14px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.43',
      'margin-bottom': '0px',
    },
    [TypographyKeys.BODY1]: {
      'font-family': 'var(--font-family)',
      'font-weight': '400',
      'font-size': '16px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.5',
      'margin-bottom': '0px',
    },
    [TypographyKeys.BODY2]: {
      'font-family': 'var(--font-family)',
      'font-weight': '700',
      'font-size': '16px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.5',
      'margin-bottom': '0px',
    },
    [TypographyKeys.BUTTON_TEXT]: {
      'font-family': 'var(--font-family)',
      'font-weight': '400',
      'font-size': '16px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.5',
      'margin-bottom': '0px',
    },
    [TypographyKeys.LABEL_TEXT]: {
      'font-family': 'var(--font-family)',
      'font-weight': '400',
      'font-size': '14px',
      'text-decoration': 'none',
      'letter-spacing': '0px',
      'line-height': '1.43',
      'margin-bottom': '0px',
    },
  },
};

const commonSkeletonStyles: TextSkeletonType = {
  'background-color': 'var(--color-on-background-muted)',
  'border-bottom-left-radius': '8px',
  'border-bottom-right-radius': '8px',
  'border-top-left-radius': '8px',
  'border-top-right-radius': '8px',
};

export const defaultGlobalThemeTextSkeletonConfig: Pick<GlobalThemeConfiguration, 'textSkeleton'> = {
  textSkeleton: [
    TypographyKeys.HEADING1,
    TypographyKeys.HEADING2,
    TypographyKeys.HEADING3,
    TypographyKeys.HEADING4,
    TypographyKeys.HEADING5,
    TypographyKeys.HEADING6,
    TypographyKeys.SUBTITLE1,
    TypographyKeys.SUBTITLE2,
    TypographyKeys.BODY1,
    TypographyKeys.BODY2,
    TypographyKeys.BUTTON_TEXT,
    TypographyKeys.LABEL_TEXT,
  ].reduce(
    (acc, key) => {
      acc[key] = { ...commonSkeletonStyles };
      return acc;
    },
    {} as Record<keyof GlobalThemeConfiguration['textSkeleton'], TextSkeletonType>,
  ),
};

type ButtonStatesType = 'active' | 'idle' | 'hover';

export const primaryButtonStatesConfig: Pick<ButtonConfigurations, ButtonStatesType> = {
  idle: {
    'background-color': 'var(--color-primary)',
    'border-color': 'var(--color-primary)',
    textColor: {
      color: 'var(--color-surface)',
    },
  },
  active: {
    'background-color': 'var(--color-active)',
    'border-color': 'var(--color-active)',
    textColor: {
      color: 'var(--color-surface)',
    },
  },
  hover: {
    'background-color': 'var(--color-hover)',
    'border-color': 'var(--color-hover)',
    textColor: {
      color: 'var(--color-surface)',
    },
  },
};

export const secondaryButtonStatesConfig: Pick<ButtonConfigurations, ButtonStatesType> = {
  idle: {
    'background-color': 'var(--color-surface)',
    'border-color': 'var(--color-primary)',
    textColor: {
      color: 'var(--color-primary)',
    },
  },
  active: {
    'background-color': 'var(--color-surface)',
    'border-color': 'var(--color-active)',
    textColor: {
      color: 'var(--color-active)',
    },
  },
  hover: {
    'background-color': 'var(--color-surface)',
    'border-color': 'var(--color-hover)',
    textColor: {
      color: 'var(--color-hover)',
    },
  },
};

export const tertiaryButtonStatesConfig: Pick<ButtonConfigurations, ButtonStatesType> = {
  idle: {
    'background-color': 'var(--color-surface)',
    'border-color': 'transparent',
    textColor: {
      color: 'var(--color-active)',
    },
  },
  active: {
    'background-color': 'var(--color-surface)',
    'border-color': 'transparent',
    textColor: {
      color: 'var(--color-active)',
    },
  },
  hover: {
    'background-color': 'var(--color-surface)',
    'border-color': 'transparent',
    textColor: {
      color: 'var(--color-hover)',
    },
  },
};

export const menuButtonStatesConfig: Pick<ButtonConfigurations, ButtonStatesType> = {
  idle: {
    'background-color': 'var(--color-surface)',
    'border-color': 'var(--color-border-color)',
    textColor: {
      color: 'var(--color-on-surface)',
    },
  },
  active: {
    'background-color': 'var(--color-surface)',
    'border-color': 'var(--color-active)',
    textColor: {
      color: 'var(--color-on-surface)',
    },
  },
  hover: {
    'background-color': 'var(--color-surface)',
    'border-color': 'var(--color-hover)',
    textColor: {
      color: 'var(--color-on-surface)',
    },
  },
};

function getButtonStateConfigByButtonType(
  buttonType: ButtonTypes,
): Pick<ButtonConfigurations, 'active' | 'idle' | 'hover'> {
  switch (buttonType) {
    case ButtonTypes.PRIMARY:
      return primaryButtonStatesConfig;
    case ButtonTypes.SECONDARY:
      return secondaryButtonStatesConfig;
    case ButtonTypes.TERTIARY:
      return tertiaryButtonStatesConfig;
    case ButtonTypes.MENU:
      return menuButtonStatesConfig;
  }
}

export function generateDefaultButtonThemeConfigurations(type: ButtonTypes): Partial<ButtonConfigurations> {
  let defaultConfig: Partial<ButtonConfigurations> = {
    ...getButtonStateConfigByButtonType(type),
    loading: {
      indicatorType: ButtonLoadingIndicatorType.ANIMATION,
    },
  };
  if (type === ButtonTypes.SECONDARY || type === ButtonTypes.TERTIARY) {
    defaultConfig = {
      ...defaultConfig,
      isLinkPrimaryButtonConfig: true,
    };
  }

  if (type !== ButtonTypes.TERTIARY) {
    defaultConfig = {
      ...defaultConfig,
      baseStyles: {
        'justify-content': 'center',
        'align-items': 'center',
        'border-style': 'solid',
        'box-shadow': '0px 0px 0px 0px rgba(0, 0, 0, 0.1)',
        'border-top-left-radius': '8px',
        'border-top-right-radius': '8px',
        'border-bottom-right-radius': '8px',
        'border-bottom-left-radius': '8px',
        'border-top-width': '1px',
        'border-bottom-width': '1px',
        'border-left-width': '1px',
        'border-right-width': '1px',
      },
      size: {
        [ButtonSizeOptions.SMALL]: {
          'padding-top': '20px',
          'padding-right': '20px',
          'padding-bottom': '20px',
          'padding-left': '20px',
          'margin-top': '0px',
          'margin-right': '0px',
          'margin-bottom': '0px',
          'margin-left': '0px',
          paddingWithIcon: {
            gap: '6px',
            'padding-right': '15px',
            'padding-left': '15px',
          },
          heightConfig: {
            heightType: ButtonHeightSizeOptions.HUG,
            height: '55',
            heightUnit: 'px',
          },
        },
        [ButtonSizeOptions.LARGE]: {
          'padding-top': '25px',
          'padding-right': '30px',
          'padding-bottom': '25px',
          'padding-left': '30px',
          'margin-top': '0px',
          'margin-right': '0px',
          'margin-bottom': '0px',
          'margin-left': '0px',
          paddingWithIcon: {
            gap: '6px',
            'padding-right': '25px',
            'padding-left': '25px',
          },
          heightConfig: {
            heightType: ButtonHeightSizeOptions.HUG,
            height: '55',
            heightUnit: 'px',
          },
        },
        [ButtonSizeOptions.FULL_WIDTH]: {
          'padding-top': '20px',
          'padding-right': '0px',
          'padding-bottom': '20px',
          'padding-left': '0px',
          'margin-top': '0px',
          'margin-right': '0px',
          'margin-bottom': '0px',
          'margin-left': '0px',
          paddingWithIcon: {
            gap: '6px',
            'padding-right': '0px',
            'padding-left': '0px',
          },
          heightConfig: {
            heightType: ButtonHeightSizeOptions.HUG,
            height: '55',
            heightUnit: 'px',
          },
        },
      },
      borderRadiusClass: BorderRadiusKeys.NONE,
    };
  }
  if (type === ButtonTypes.MENU) {
    defaultConfig = {
      ...defaultConfig,
      iconConfigurations: {
        type: 'right',
        iconName: 'arrow-right-jamboo',
        height: '18px',
        width: '18px',
        color: 'var(--color-on-surface)',
      },
    };
  }
  return defaultConfig;
}

export const defaultGlobalThemeRadiusItemConfig: BorderRadiusStyles = {
  'border-top-left-radius': '8px',
  'border-top-right-radius': '8px',
  'border-bottom-right-radius': '8px',
  'border-bottom-left-radius': '8px',
};

export const defaultGlobalThemeRadiusTwoItemConfig: BorderRadiusStyles = {
  'border-top-left-radius': '25px',
  'border-top-right-radius': '25px',
  'border-bottom-right-radius': '25px',
  'border-bottom-left-radius': '25px',
};

export const defaultTooltipStyles: TooltipStyleConfigurations = {
  'align-items': 'center',
  'justify-content': 'start',
  'padding-top': '8px',
  'padding-right': '4px',
  'padding-bottom': '8px',
  'padding-left': '4px',
  'border-top-left-radius': '5px',
  'border-top-right-radius': '5px',
  'border-bottom-right-radius': '5px',
  'border-bottom-left-radius': '5px',
  'border-top-width': '1px',
  'border-left-width': '1px',
  'border-bottom-width': '1px',
  'border-right-width': '1px',
  'background-color': 'var(--color-surface)',
  'border-color': 'var(--color-border-color)',
  'box-shadow': '0px 0px 0px 0px rgba(0, 0, 0, 0.1)',
  'border-style': 'solid',
  color: 'var(--color-on-surface-muted)',
  textStyleClass: 'labelText',
  borderRadiusClass: BorderRadiusKeys.NONE,
};

export const defaultConfirmationDialogStyles: ConfirmationDialogStyleConfigurations = {
  'background-color': 'var(--color-surface)',
  'border-color': 'var(--color-border-color)',
  borderRadiusClass: BorderRadiusKeys.RADIUS_ONE,
  'border-top-left-radius': '8px',
  'border-top-right-radius': '8px',
  'border-bottom-right-radius': '8px',
  'border-bottom-left-radius': '8px',
  'border-top-width': '1px',
  'border-right-width': '1px',
  'border-bottom-width': '1px',
  'border-left-width': '1px',
  'border-style': 'solid',
  'box-shadow': '0px 0px 0px 0px rgba(0, 0, 0, 0.1)',
  titleTextStyleClass: TypographyKeys.BODY1,
  bodyTextStyleClass: TypographyKeys.BODY1,
  titleColor: 'var(--color-on-surface)',
  bodyColor: 'var(--color-on-surface)',
  positiveButtonClass: `${ButtonTypes.PRIMARY} ${ButtonSizeOptions.SMALL}`,
  negativeButtonClass: `${ButtonTypes.SECONDARY} ${ButtonSizeOptions.SMALL}`,
};

export const defaultInputFieldConfigs: InputFieldsConfigurations = {
  idle: {
    'background-color': 'var(--color-surface)',
    'border-color': 'var(--color-border-color)',
    textColor: {
      color: 'var(--color-on-surface-muted)',
    },
    labelColor: {
      color: 'var(--color-on-surface-muted)',
    },
  },
  active: {
    'background-color': 'var(--color-surface)',
    'border-color': 'var(--color-active)',
    textColor: {
      color: 'var(--color-on-surface-muted)',
    },
    labelColor: {
      color: 'var(--color-on-surface-muted)',
    },
  },
  labelConfig: {
    type: InputLabelOptions.STATIC,
    'align-items': 'center',
    'justify-content': 'start',
    'padding-top': '10px',
    'padding-right': '5px',
    'padding-bottom': '10px',
    'padding-left': '5px',
  },
  inputStyles: {
    'align-items': 'center',
    'justify-content': 'start',
    'padding-top': '8px',
    'padding-right': '4px',
    'padding-bottom': '8px',
    'padding-left': '4px',
    height: '40px',
    width: '100%',
    'border-top-left-radius': '5px',
    'border-top-right-radius': '5px',
    'border-bottom-right-radius': '5px',
    'border-bottom-left-radius': '5px',
    'border-top-width': '1px',
    'border-left-width': '1px',
    'border-bottom-width': '1px',
    'border-right-width': '1px',
    'box-shadow': '0px 0px 0px 0px rgba(0, 0, 0, 0.1)',
    'border-style': 'solid',
  },
  textStyleClass: 'body1',
  borderRadiusClass: BorderRadiusKeys.RADIUS_ONE,
  otpInputFieldSpacing: {
    'margin-right': '5px',
  },
  finxForm: { gap: '5px' },
};

export const defaultGlobalThemeRadiusConfig: {
  [key in Exclude<BorderRadiusKeys, BorderRadiusKeys.NONE>]: BorderRadiusStyles;
} = {
  [BorderRadiusKeys.RADIUS_ONE]: defaultGlobalThemeRadiusItemConfig,
  [BorderRadiusKeys.RADIUS_TWO]: defaultGlobalThemeRadiusTwoItemConfig,
};

export const globalThemeConstant: GlobalThemeConfiguration = {
  themeName: 'Default',
  generalConfigurations: defaultGlobalThemeGeneralConfig.generalConfigurations,
  buttons: {
    [ButtonTypes.PRIMARY]: generateDefaultButtonThemeConfigurations(ButtonTypes.PRIMARY),
    [ButtonTypes.SECONDARY]: generateDefaultButtonThemeConfigurations(ButtonTypes.SECONDARY),
    [ButtonTypes.TERTIARY]: generateDefaultButtonThemeConfigurations(ButtonTypes.TERTIARY),
    [ButtonTypes.MENU]: generateDefaultButtonThemeConfigurations(ButtonTypes.MENU),
  },
  typography: defaultGlobalThemeTypographyConfig.typography,
  textSkeleton: defaultGlobalThemeTextSkeletonConfig.textSkeleton,
  inputFields: defaultInputFieldConfigs,
  radius: defaultGlobalThemeRadiusConfig,
  tooltipConfigurations: defaultTooltipStyles,
  confirmationDialogConfigurations: defaultConfirmationDialogStyles,
} as GlobalThemeConfiguration;

const buttonWidgets = [
  WidgetNames.QUICK_LINKS_WIDGET,
  WidgetNames.MULTI_ICON_QUICK_LINK_WIDGET,
  WidgetNames.CTA_BUTTON_WIDGET,
  WidgetNames.BACK_BUTTON_WIDGET,
  WidgetNames.MANDATORY_CTA_BUTTON,
  WidgetNames.FORM_SUBMISSION_BUTTON,
  WidgetNames.ONBOARDING_BACK_NEXT_BUTTON,
  WidgetNames.WIDGET_NESTED_BUTTON,
  WidgetNames.ONBOARDING_BUTTON,
];

export const onSetDefaultButtonGlobalStyling = (widget: NewWorkflowWidgetConfig) => {
  if (buttonWidgets.includes(widget.name as WidgetNames)) {
    widget = {
      ...widget,
      widgetConfiguration: {
        ...widget.widgetConfiguration,
        attributes: {
          ...widget.widgetConfiguration?.attributes,
          widgetProperties: {
            ...widget.widgetConfiguration?.attributes?.widgetProperties,
            globalStyling: true,
            globalStylingClass:
              widget.name === WidgetNames.MULTI_ICON_QUICK_LINK_WIDGET
                ? `${ButtonTypes.MENU} ${ButtonSizeOptions.FULL_WIDTH}`
                : `${ButtonTypes.PRIMARY} ${ButtonSizeOptions.FULL_WIDTH}`,
          },
        },
      },
    };
  }

  return widget;
};
